'use client';

/**
 * DashboardPdfButton — Phase F2 Sprint 2.
 *
 * "Export as PDF" toolbar button for dashboard surfaces. Closes the
 * Phase C audit gap: 0/6 dashboards had any PDF export affordance.
 *
 * Mechanism — three modes (consumer picks one):
 *   1. PRINT (default) — invokes window.print() on the current page.
 *      Pairs with the existing /print sub-routes shipped in Phase E.
 *   2. NAVIGATE — navigates to a /print URL the consumer supplies;
 *      that route renders inside the PrintView primitive and the user
 *      triggers print there.
 *   3. CALLBACK — custom onExport callback for bespoke PDF flows.
 *
 * Mode 2 is the recommended path for dashboards: the /print route can
 * include richer chrome (branded header, generated-at timestamp, table
 * formatting) than the dashboard's screen UI.
 */

import React from 'react';
import { FileDown } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface DashboardPdfButtonProps {
  /**
   * Mode 1 / default — calling window.print() on the current page.
   * Either set `printHref` (mode 2) or `onExport` (mode 3) to override.
   */
  printHref?: string;
  /** Override label. Defaults to "Export PDF". */
  label?: string;
  /** Custom export handler. When supplied, takes precedence over printHref. */
  onExport?: () => void | Promise<void>;
  /** Disable the button. */
  disabled?: boolean;
  className?: string;
}

export function DashboardPdfButton({
  printHref,
  label = 'Export PDF',
  onExport,
  disabled,
  className,
}: DashboardPdfButtonProps) {
  function handleClick() {
    if (onExport) {
      onExport();
      return;
    }
    if (printHref) {
      // Open the /print route in a new tab so the user keeps the
      // dashboard context. The /print page invokes window.print()
      // itself (typically via Phase E PrintTriggerButton).
      window.open(printHref, '_blank', 'noopener,noreferrer');
      return;
    }
    // Mode 1 — print the current dashboard surface.
    window.print();
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 text-[11.5px] font-medium text-statura-ink',
        'hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      data-dashboard-pdf-button
    >
      <FileDown className="h-3 w-3" aria-hidden="true" />
      {label}
    </button>
  );
}
